import { useState, useEffect } from 'react';
import { billingAPI, proceduresAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { Settings, Plus, Trash2, Edit, Receipt, CalendarDays, FileText } from 'lucide-react';

export const BillingConfigPage = () => {
  const [config, setConfig] = useState(null);
  const [procedures, setProcedures] = useState([]);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Procedure form
  const [isProcOpen, setIsProcOpen] = useState(false);
  const [procForm, setProcForm] = useState({ name: '', code: '', base_price: '', category: 'procedure', gst_applicable: true });
  const [procSubmitting, setProcSubmitting] = useState(false);
  // v36 — Edit procedure dialog
  const [isEditProcOpen, setIsEditProcOpen] = useState(false);
  const [editProcForm, setEditProcForm] = useState(null);  // null = closed; populated when Edit clicked

  // Leave type form
  const [isLeaveOpen, setIsLeaveOpen] = useState(false);
  const [leaveForm, setLeaveForm] = useState({ name: '', code: '', days_allowed: 12, carry_forward: false, encashable: false });
  const [leaveSubmitting, setLeaveSubmitting] = useState(false);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [cfgRes, procRes, ltRes] = await Promise.all([
        billingAPI.getConfig(),
        proceduresAPI.list(),
        billingAPI.getLeaveTypes(),
      ]);
      setConfig(cfgRes.data);
      setProcedures(procRes.data || []);
      setLeaveTypes(ltRes.data || []);
    } catch (err) {
      toast.error('Failed to load billing config');
    } finally {
      setLoading(false);
    }
  };

  const saveConfig = async () => {
    setSaving(true);
    try {
      const res = await billingAPI.updateConfig(config);
      setConfig(res.data);
      toast.success('Billing configuration saved');
    } catch (err) {
      toast.error('Failed to save config');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateProcedure = async (e) => {
    e.preventDefault();
    setProcSubmitting(true);
    try {
      await proceduresAPI.create({
        ...procForm,
        base_price: parseFloat(procForm.base_price) || 0,
      });
      toast.success('Procedure added');
      setIsProcOpen(false);
      setProcForm({ name: '', code: '', base_price: '', category: 'procedure', gst_applicable: true });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally {
      setProcSubmitting(false);
    }
  };

  // v36 — Edit Procedure
  const openEditProc = (p) => {
    setEditProcForm({
      id: p.id,
      name: p.name || '',
      code: p.code || '',
      base_price: p.base_price ?? '',
      category: p.category || 'procedure',
      gst_applicable: p.gst_applicable !== false,
    });
    setIsEditProcOpen(true);
  };
  const handleUpdateProcedure = async (e) => {
    e.preventDefault();
    if (!editProcForm) return;
    setProcSubmitting(true);
    try {
      const { id, ...payload } = editProcForm;
      payload.base_price = parseFloat(payload.base_price) || 0;
      await proceduresAPI.update(id, payload);
      toast.success('Procedure updated');
      setIsEditProcOpen(false);
      setEditProcForm(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    } finally {
      setProcSubmitting(false);
    }
  };

  const deleteProcedure = async (id) => {
    if (!window.confirm('Delete this procedure?')) return;
    try {
      await proceduresAPI.delete(id);
      toast.success('Procedure deleted');
      fetchData();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const handleCreateLeaveType = async (e) => {
    e.preventDefault();
    setLeaveSubmitting(true);
    try {
      await billingAPI.createLeaveType(leaveForm);
      toast.success('Leave type added');
      setIsLeaveOpen(false);
      setLeaveForm({ name: '', code: '', days_allowed: 12, carry_forward: false, encashable: false });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally {
      setLeaveSubmitting(false);
    }
  };

  const deleteLeaveType = async (id) => {
    if (!window.confirm('Delete this leave type?')) return;
    try {
      await billingAPI.deleteLeaveType(id);
      toast.success('Leave type deleted');
      fetchData();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  if (loading) return <div className="space-y-6"><Skeleton className="h-10 w-64" /><Skeleton className="h-40" /></div>;

  return (
    <div className="space-y-6" data-testid="billing-config-page">
      <div>
        <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Billing & Configuration</h1>
        <p className="mt-1 text-slate-600">Manage GST, procedures, discounts, and leave types</p>
      </div>

      <Tabs defaultValue="gst" className="space-y-4">
        <TabsList className="bg-slate-100">
          <TabsTrigger value="gst" data-testid="tab-gst"><Settings className="h-4 w-4 mr-2" /> GST & Billing</TabsTrigger>
          <TabsTrigger value="procedures" data-testid="tab-procedures"><FileText className="h-4 w-4 mr-2" /> Procedures ({procedures.length})</TabsTrigger>
          <TabsTrigger value="leave" data-testid="tab-leave-types"><CalendarDays className="h-4 w-4 mr-2" /> Leave Types ({leaveTypes.length})</TabsTrigger>
        </TabsList>

        {/* GST & Billing Config */}
        <TabsContent value="gst">
          <Card>
            <CardHeader><CardTitle className="text-lg">GST & Discount Settings</CardTitle></CardHeader>
            <CardContent className="space-y-6">
              {config && (
                <>
                  <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                    <div>
                      <p className="font-medium">Enable GST</p>
                      <p className="text-sm text-slate-500">Apply GST on invoices</p>
                    </div>
                    <Switch checked={config.gst_enabled} onCheckedChange={v => setConfig({ ...config, gst_enabled: v })} data-testid="gst-toggle" />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label>GST Percentage (%)</Label>
                      <Input type="number" value={config.gst_percentage} onChange={e => setConfig({ ...config, gst_percentage: parseFloat(e.target.value) || 0 })} data-testid="gst-percentage-input" />
                    </div>
                    <div className="space-y-2">
                      <Label>GSTIN Number</Label>
                      <Input value={config.gstin || ''} onChange={e => setConfig({ ...config, gstin: e.target.value })} placeholder="e.g., 29AABCT1332L1ZB" data-testid="gstin-input" />
                    </div>
                    <div className="space-y-2">
                      <Label>Default Discount Type</Label>
                      <Select value={config.default_discount_type} onValueChange={v => setConfig({ ...config, default_discount_type: v })}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="percentage">Percentage</SelectItem>
                          <SelectItem value="flat">Flat Amount</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <Button onClick={saveConfig} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="save-config-btn">
                    {saving ? 'Saving...' : 'Save Configuration'}
                  </Button>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Procedures */}
        <TabsContent value="procedures">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">Procedure Catalog</CardTitle>
                <Dialog open={isProcOpen} onOpenChange={setIsProcOpen}>
                  <DialogTrigger asChild>
                    <Button className="bg-emerald-600 hover:bg-emerald-700" data-testid="add-procedure-btn"><Plus className="h-4 w-4 mr-2" /> Add Procedure</Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader><DialogTitle>Add Procedure</DialogTitle></DialogHeader>
                    <form onSubmit={handleCreateProcedure}>
                      <div className="space-y-4 py-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label>Name *</Label>
                            <Input value={procForm.name} onChange={e => setProcForm({ ...procForm, name: e.target.value })} required data-testid="proc-name-input" />
                          </div>
                          <div className="space-y-2">
                            <Label>Code</Label>
                            <Input value={procForm.code} onChange={e => setProcForm({ ...procForm, code: e.target.value.toUpperCase() })} placeholder="AUTO" />
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label>Base Price (Rs.)</Label>
                            <Input
                              type="number"
                              min="0"
                              step="0.01"
                              value={procForm.base_price}
                              onChange={e => setProcForm({ ...procForm, base_price: e.target.value })}
                              placeholder="0"
                              data-testid="proc-price-input"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Category</Label>
                            <Select value={procForm.category} onValueChange={v => setProcForm({ ...procForm, category: v })}>
                              <SelectTrigger><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="procedure">Procedure</SelectItem>
                                <SelectItem value="lab">Lab Test</SelectItem>
                                <SelectItem value="misc">Miscellaneous</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <Switch checked={procForm.gst_applicable} onCheckedChange={v => setProcForm({ ...procForm, gst_applicable: v })} />
                          <Label>GST Applicable</Label>
                        </div>
                      </div>
                      <DialogFooter>
                        <Button type="button" variant="outline" onClick={() => setIsProcOpen(false)}>Cancel</Button>
                        <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700" disabled={procSubmitting} data-testid="proc-submit-btn">
                          {procSubmitting ? 'Adding...' : 'Add Procedure'}
                        </Button>
                      </DialogFooter>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent>
              {procedures.length === 0 ? (
                <div className="text-center py-8 text-slate-500"><FileText className="h-10 w-10 mx-auto mb-3 text-slate-300" /><p>No procedures configured</p></div>
              ) : (
                <div className="grid gap-2">
                  {procedures.map(p => (
                    <div key={p.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg" data-testid={`proc-${p.id}`}>
                      <div className="flex items-center gap-4">
                        <div>
                          <p className="font-medium text-slate-900">{p.name}</p>
                          <div className="flex items-center gap-2 mt-0.5">
                            <Badge variant="outline" className="text-xs">{p.code}</Badge>
                            <Badge className="text-xs bg-blue-100 text-blue-700">{p.category}</Badge>
                            {p.gst_applicable && <Badge className="text-xs bg-amber-100 text-amber-700">GST</Badge>}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <span className="font-semibold text-slate-900">Rs. {p.base_price}</span>
                        <Button variant="ghost" size="sm" className="text-blue-600 hover:text-blue-700 hover:bg-blue-50" onClick={() => openEditProc(p)} data-testid={`edit-proc-${p.id}`} title="Edit procedure"><Edit className="h-4 w-4" /></Button>
                        <Button variant="ghost" size="sm" className="text-red-500" onClick={() => deleteProcedure(p.id)} data-testid={`delete-proc-${p.id}`} title="Delete procedure"><Trash2 className="h-4 w-4" /></Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* v36 — Edit Procedure Dialog */}
        <Dialog open={isEditProcOpen} onOpenChange={(o) => { setIsEditProcOpen(o); if (!o) setEditProcForm(null); }}>
          <DialogContent data-testid="edit-procedure-dialog">
            <DialogHeader><DialogTitle>Edit Procedure</DialogTitle></DialogHeader>
            {editProcForm && (
              <form onSubmit={handleUpdateProcedure}>
                <div className="space-y-4 py-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Name *</Label>
                      <Input value={editProcForm.name} onChange={e => setEditProcForm({ ...editProcForm, name: e.target.value })} required data-testid="edit-proc-name-input" />
                    </div>
                    <div className="space-y-2">
                      <Label>Code</Label>
                      <Input value={editProcForm.code} onChange={e => setEditProcForm({ ...editProcForm, code: e.target.value.toUpperCase() })} data-testid="edit-proc-code-input" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Base Price (Rs.)</Label>
                      <Input type="number" min="0" step="0.01" value={editProcForm.base_price} onChange={e => setEditProcForm({ ...editProcForm, base_price: e.target.value })} placeholder="0" data-testid="edit-proc-price-input" />
                    </div>
                    <div className="space-y-2">
                      <Label>Category</Label>
                      <Select value={editProcForm.category} onValueChange={v => setEditProcForm({ ...editProcForm, category: v })}>
                        <SelectTrigger data-testid="edit-proc-category-trigger"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="procedure">Procedure</SelectItem>
                          <SelectItem value="lab">Lab Test</SelectItem>
                          <SelectItem value="misc">Miscellaneous</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Switch checked={editProcForm.gst_applicable} onCheckedChange={v => setEditProcForm({ ...editProcForm, gst_applicable: v })} data-testid="edit-proc-gst-switch" />
                    <Label>GST Applicable</Label>
                  </div>
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => { setIsEditProcOpen(false); setEditProcForm(null); }}>Cancel</Button>
                  <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700" disabled={procSubmitting} data-testid="edit-proc-submit-btn">
                    {procSubmitting ? 'Saving…' : 'Save Changes'}
                  </Button>
                </DialogFooter>
              </form>
            )}
          </DialogContent>
        </Dialog>

        {/* Leave Types */}
        <TabsContent value="leave">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">Leave Types</CardTitle>
                <Dialog open={isLeaveOpen} onOpenChange={setIsLeaveOpen}>
                  <DialogTrigger asChild>
                    <Button className="bg-emerald-600 hover:bg-emerald-700" data-testid="add-leave-type-btn"><Plus className="h-4 w-4 mr-2" /> Add Leave Type</Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader><DialogTitle>Add Leave Type</DialogTitle></DialogHeader>
                    <form onSubmit={handleCreateLeaveType}>
                      <div className="space-y-4 py-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2"><Label>Name *</Label><Input value={leaveForm.name} onChange={e => setLeaveForm({ ...leaveForm, name: e.target.value })} required data-testid="lt-name-input" /></div>
                          <div className="space-y-2"><Label>Code *</Label><Input value={leaveForm.code} onChange={e => setLeaveForm({ ...leaveForm, code: e.target.value.toUpperCase() })} required /></div>
                        </div>
                        <div className="space-y-2"><Label>Days Allowed (per year)</Label><Input type="number" value={leaveForm.days_allowed} onChange={e => setLeaveForm({ ...leaveForm, days_allowed: parseInt(e.target.value) || 0 })} data-testid="lt-days-input" /></div>
                        <div className="flex gap-6">
                          <div className="flex items-center gap-2"><Switch checked={leaveForm.carry_forward} onCheckedChange={v => setLeaveForm({ ...leaveForm, carry_forward: v })} /><Label>Carry Forward</Label></div>
                          <div className="flex items-center gap-2"><Switch checked={leaveForm.encashable} onCheckedChange={v => setLeaveForm({ ...leaveForm, encashable: v })} /><Label>Encashable</Label></div>
                        </div>
                      </div>
                      <DialogFooter>
                        <Button type="button" variant="outline" onClick={() => setIsLeaveOpen(false)}>Cancel</Button>
                        <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700" disabled={leaveSubmitting} data-testid="lt-submit-btn">{leaveSubmitting ? 'Adding...' : 'Add Leave Type'}</Button>
                      </DialogFooter>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent>
              {leaveTypes.length === 0 ? (
                <div className="text-center py-8 text-slate-500"><CalendarDays className="h-10 w-10 mx-auto mb-3 text-slate-300" /><p>No leave types configured</p></div>
              ) : (
                <div className="grid gap-2">
                  {leaveTypes.map(lt => (
                    <div key={lt.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg" data-testid={`lt-${lt.id}`}>
                      <div>
                        <p className="font-medium text-slate-900">{lt.name}</p>
                        <div className="flex items-center gap-2 mt-0.5">
                          <Badge variant="outline" className="text-xs">{lt.code}</Badge>
                          <span className="text-xs text-slate-500">{lt.days_allowed} days/year</span>
                          {lt.carry_forward && <Badge className="text-xs bg-blue-100 text-blue-700">Carry Forward</Badge>}
                          {lt.encashable && <Badge className="text-xs bg-emerald-100 text-emerald-700">Encashable</Badge>}
                        </div>
                      </div>
                      <Button variant="ghost" size="sm" className="text-red-500" onClick={() => deleteLeaveType(lt.id)}><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default BillingConfigPage;
